import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Pencil, ShieldAlert } from 'lucide-react';
import api, { errorMessage } from '../services/api';
import {
  Card,
  Button,
  Badge,
  CopyButton,
  InfoRow,
  Sheet,
  Field,
  OrderStatusBadge,
  SectionTitle,
  LoadingBlock,
  ErrorBanner,
  EmptyState,
  Avatar,
  inputClass
} from '../components/ui';
import { formatCurrency, formatDateTime, fromUnix, formatNumber } from '../utils/format';

const ITEM_STATUS_TONE = { COMPLETED: 'green', 'COMPLETED-PARTIAL REFUNDED': 'green', PENDING: 'yellow', CANCELLED: 'red' };

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchOrder = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await api.get(`/admin/orders/${id}`);
      setOrder(res.data.data.order);
    } catch (err) {
      setError(errorMessage(err, 'Lỗi khi tải chi tiết đơn hàng'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEdit = () => {
    setForm({
      orderStatus: order.orderStatus || '',
      commission_rate: order.commission_rate ?? '',
      is_withdrawable: !!order.is_withdrawable,
      shop_name: order.shop_name || ''
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { orderStatus, ...rest } = form;
      const payload = {
        ...rest,
        order_status: orderStatus,
        commission_rate: form.commission_rate === '' ? null : Number(form.commission_rate)
      };
      const res = await api.patch(`/admin/orders/${id}`, payload);
      setOrder(res.data.data.order);
      setEditing(false);
      toast.success('Đã cập nhật đơn hàng');
    } catch (err) {
      toast.error(errorMessage(err, 'Lỗi khi cập nhật đơn hàng'));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBanner message={error} onRetry={fetchOrder} />;
  if (!order) return <EmptyState title="Không tìm thấy đơn hàng" />;

  const fraudCount = order.fraudStats?.fraud || 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-2">
        <Button variant="ghost" size="icon" className="-ml-2 flex-shrink-0" onClick={() => navigate(-1)} aria-label="Quay lại">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <h1 className="truncate font-mono text-lg font-bold text-gray-900 md:text-xl">{order.order_sn}</h1>
            <CopyButton value={order.order_sn} />
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <OrderStatusBadge status={order.orderStatus} />
            <Badge tone={order.is_withdrawable ? 'indigo' : 'gray'}>{order.is_withdrawable ? 'Đã duyệt rút' : 'Chưa duyệt rút'}</Badge>
            {order.source === 'lost_order' && <Badge tone="orange">Từ đơn thất lạc</Badge>}
            {fraudCount > 0 && <Badge tone="red">{fraudCount} SP gian lận</Badge>}
          </div>
        </div>
        <Button variant="outline" size="sm" className="flex-shrink-0" onClick={openEdit}>
          <Pencil className="h-4 w-4" /> Sửa
        </Button>
      </div>

      {/* Tóm tắt */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card className="p-3.5">
          <p className="text-xs text-gray-500">Hoàn cho user</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(order.totalCommission)}</p>
        </Card>
        <Card className="p-3.5">
          <p className="text-xs text-gray-500">Giá trị đơn</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(order.totalAmount)}</p>
        </Card>
        <Card className="p-3.5">
          <p className="text-xs text-gray-500">Sản phẩm</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{formatNumber(order.totalItems)}</p>
        </Card>
        <Card className="p-3.5">
          <p className="text-xs text-gray-500">Số lượng</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{formatNumber(order.totalQuantity)}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <section>
          <SectionTitle>Thông tin đơn</SectionTitle>
          <Card className="divide-y divide-gray-100 px-4">
            <InfoRow label="Shop">{order.shop_name || '—'}</InfoRow>
            <InfoRow label="Tỷ lệ hoàn">{order.commission_rate != null ? `${order.commission_rate}%` : '—'}</InfoRow>
            <InfoRow label="Nguồn">{order.source === 'lost_order' ? 'Đơn thất lạc' : 'Import Shopee'}</InfoRow>
            <InfoRow label="Đặt hàng">{formatDateTime(fromUnix(order.pending_date))}</InfoRow>
            <InfoRow label="Hoàn thành">{formatDateTime(fromUnix(order.complete_time))}</InfoRow>
            <InfoRow label="Duyệt rút">{formatDateTime(fromUnix(order.approved_date))}</InfoRow>
            <InfoRow label="Import lúc">{formatDateTime(order.created_at)}</InfoRow>
            <InfoRow label="Cập nhật">{formatDateTime(order.updated_at)}</InfoRow>
            <InfoRow label="Order ID" mono>{order.id}</InfoRow>
          </Card>
        </section>

        <section className="space-y-5">
          {order.user && (
            <div>
              <SectionTitle>Người dùng</SectionTitle>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <Avatar name={order.user.name} email={order.user.email} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">{order.user.name || '—'}</p>
                    <p className="truncate text-xs text-gray-500">{order.user.email}</p>
                  </div>
                  <CopyButton value={order.user.id} label="Sao chép user ID" />
                </div>
                <Link to={`/orders?user_id=${order.user.id}`} className="mt-3 block rounded-xl bg-gray-50 py-2.5 text-center text-sm font-medium text-gray-900 hover:bg-gray-100">
                  Xem tất cả đơn của user
                </Link>
              </Card>
            </div>
          )}

          {order.userOrder && (
            <div>
              <SectionTitle>Lượt click gốc</SectionTitle>
              <Card className="divide-y divide-gray-100 px-4">
                <InfoRow label="User order ID" mono>{order.userOrder.id}</InfoRow>
                <InfoRow label="Sub ID" mono>{order.userOrder.subid || '—'}</InfoRow>
                <InfoRow label="Item ID" mono>{order.userOrder.item_id || '—'}</InfoRow>
                <InfoRow label="Xác thực">{order.userOrder.verified ? 'Rồi' : 'Chưa'}</InfoRow>
                <InfoRow label="Click lúc">{formatDateTime(order.userOrder.created_at)}</InfoRow>
              </Card>
            </div>
          )}
        </section>
      </div>

      <section>
        <SectionTitle>Sản phẩm ({order.items?.length || 0})</SectionTitle>
        {order.items?.length ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {order.items.map((item) => {
              const st = String(item.displayItemStatus || '').toUpperCase();
              return (
                <Card key={item.id} className="p-3.5">
                  <div className="flex gap-3">
                    {item.img_code ? (
                      <img
                        src={item.img_code}
                        alt=""
                        className="h-14 w-14 flex-shrink-0 rounded-xl object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="h-14 w-14 flex-shrink-0 rounded-xl bg-gray-100" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-sm font-medium text-gray-900">{item.item_name || '—'}</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        <Badge tone={ITEM_STATUS_TONE[st] || 'gray'}>{item.displayItemStatus || 'UNKNOWN'}</Badge>
                        {item.is_fraud ? (
                          <Badge tone="red"><ShieldAlert className="mr-1 h-3 w-3" />Gian lận</Badge>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 border-t border-gray-100 pt-2 text-xs">
                    <div>
                      <p className="text-gray-500">SL</p>
                      <p className="font-medium text-gray-900">{item.qty}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Giá</p>
                      <p className="font-medium text-gray-900">{formatCurrency(item.actual_amount)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-500">Hoàn</p>
                      <p className="font-semibold text-gray-900">{formatCurrency(item.itemTotalCommission)}</p>
                    </div>
                  </div>
                  <p className="mt-1 truncate font-mono text-[11px] text-gray-400">item {item.item_id || '—'} · model {item.model_id || '—'}</p>
                </Card>
              );
            })}
          </div>
        ) : (
          <EmptyState title="Không có sản phẩm" />
        )}
      </section>

      <Sheet
        open={editing}
        onClose={() => !saving && setEditing(false)}
        title="Sửa đơn hàng"
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>Hủy</Button>
            <Button onClick={save} loading={saving}>Lưu</Button>
          </div>
        }
      >
        <div className="space-y-3">
          <Field label="Trạng thái đơn">
            <select className={inputClass} value={form.orderStatus || ''} onChange={(e) => setForm((f) => ({ ...f, orderStatus: e.target.value }))}>
              <option value="">— Chọn —</option>
              <option value="PENDING">PENDING</option>
              <option value="COMPLETED">COMPLETED</option>
              <option value="CANCELLED">CANCELLED</option>
              <option value="PROCESSING">PROCESSING</option>
            </select>
          </Field>
          <Field label="Duyệt cho rút tiền" hint="Đổi mục này sẽ tính lại số dư của user ngay.">
            <select
              className={inputClass}
              value={form.is_withdrawable ? 'true' : 'false'}
              onChange={(e) => setForm((f) => ({ ...f, is_withdrawable: e.target.value === 'true' }))}
            >
              <option value="false">Chưa duyệt</option>
              <option value="true">Đã duyệt</option>
            </select>
          </Field>
          <Field label="Tỷ lệ hoàn (%)" hint="Áp dụng khi cron cập nhật hoa hồng đơn này lần sau.">
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              max="100"
              className={inputClass}
              value={form.commission_rate}
              onChange={(e) => setForm((f) => ({ ...f, commission_rate: e.target.value }))}
            />
          </Field>
          <Field label="Tên shop">
            <input className={inputClass} value={form.shop_name || ''} onChange={(e) => setForm((f) => ({ ...f, shop_name: e.target.value }))} />
          </Field>
        </div>
      </Sheet>
    </div>
  );
};

export default OrderDetailPage;
